<script setup lang="ts">
import { ref, computed } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = useRouter()
const authStore = useAuthStore()

const isAuthenticated = computed(() => authStore.isAuthenticated)
const user = computed(() => authStore.user)
const mobileMenuOpen = ref(false)

async function handleLogout() {
  await authStore.logout()
  mobileMenuOpen.value = false
  router.push('/')
}

function closeMobile() {
  mobileMenuOpen.value = false
}
</script>

<template>
  <header class="bg-gray-950 border-b border-gray-800 sticky top-0 z-50">
    <nav class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between h-14 items-center">
        <!-- Logo -->
        <RouterLink to="/" class="flex items-center space-x-2" @click="closeMobile">
          <img src="@/assets/logo.svg" alt="" class="w-6 h-6" />
          <span class="text-lg font-bold text-emerald-500">Prisoner's Dilemma</span>
        </RouterLink>

        <!-- Desktop Navigation -->
        <div class="hidden md:flex items-center space-x-6">
          <RouterLink
            to="/play"
            class="text-gray-400 hover:text-gray-100 transition-colors text-sm"
            active-class="text-emerald-400"
          >
            Play
          </RouterLink>
          <RouterLink
            to="/marketplace"
            class="text-gray-400 hover:text-gray-100 transition-colors text-sm"
            active-class="text-emerald-400"
          >
            Marketplace <span class="text-gray-600 text-xs">(Soon)</span>
          </RouterLink>
          <template v-if="isAuthenticated">
            <RouterLink
              to="/editor"
              class="text-gray-400 hover:text-gray-100 transition-colors text-sm"
              active-class="text-emerald-400"
            >
              My Players
            </RouterLink>
            <RouterLink
              to="/settings"
              class="text-gray-400 hover:text-gray-100 transition-colors text-sm"
              active-class="text-emerald-400"
            >
              Settings
            </RouterLink>
          </template>

          <div class="h-4 w-px bg-gray-800" />

          <template v-if="isAuthenticated">
            <span class="text-gray-500 text-sm">{{ user?.email }}</span>
            <button class="btn btn-ghost text-sm" @click="handleLogout">
              Logout
            </button>
          </template>
          <template v-else>
            <RouterLink
              to="/login"
              class="text-gray-400 hover:text-gray-100 transition-colors text-sm"
            >
              Login
            </RouterLink>
            <RouterLink to="/register" class="btn btn-primary text-sm">
              Get Started
            </RouterLink>
          </template>
        </div>

        <!-- Mobile hamburger -->
        <button
          class="md:hidden text-gray-400 hover:text-gray-100 transition-colors"
          @click="mobileMenuOpen = !mobileMenuOpen"
        >
          <svg v-if="!mobileMenuOpen" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Mobile menu -->
      <div v-if="mobileMenuOpen" class="md:hidden pb-4 space-y-2">
        <RouterLink
          to="/play"
          class="block px-3 py-2 text-gray-400 hover:text-gray-100 hover:bg-gray-800/50 rounded-md text-sm"
          @click="closeMobile"
        >
          Play
        </RouterLink>
        <RouterLink
          to="/marketplace"
          class="block px-3 py-2 text-gray-400 hover:text-gray-100 hover:bg-gray-800/50 rounded-md text-sm"
          @click="closeMobile"
        >
          Marketplace <span class="text-gray-600 text-xs">(Soon)</span>
        </RouterLink>
        <template v-if="isAuthenticated">
          <RouterLink
            to="/editor"
            class="block px-3 py-2 text-gray-400 hover:text-gray-100 hover:bg-gray-800/50 rounded-md text-sm"
            @click="closeMobile"
          >
            My Players
          </RouterLink>
          <RouterLink
            to="/settings"
            class="block px-3 py-2 text-gray-400 hover:text-gray-100 hover:bg-gray-800/50 rounded-md text-sm"
            @click="closeMobile"
          >
            Settings
          </RouterLink>
          <div class="border-t border-gray-800 pt-2 mt-2">
            <span class="block px-3 py-1 text-gray-500 text-sm">{{ user?.email }}</span>
            <button
              class="block w-full text-left px-3 py-2 text-gray-400 hover:text-gray-100 hover:bg-gray-800/50 rounded-md text-sm"
              @click="handleLogout"
            >
              Logout
            </button>
          </div>
        </template>
        <template v-else>
          <div class="border-t border-gray-800 pt-2 mt-2">
            <RouterLink
              to="/login"
              class="block px-3 py-2 text-gray-400 hover:text-gray-100 hover:bg-gray-800/50 rounded-md text-sm"
              @click="closeMobile"
            >
              Login
            </RouterLink>
            <RouterLink
              to="/register"
              class="block px-3 py-2 text-emerald-400 hover:bg-gray-800/50 rounded-md text-sm"
              @click="closeMobile"
            >
              Get Started
            </RouterLink>
          </div>
        </template>
      </div>
    </nav>
  </header>
</template>
